/**
 * About + Contact combined page. Active tab syncs with URL query ?tab=about|contact
 * (default /contact opens Contact Us form; ?tab=about opens About Us).
 * Contact form POSTs to /api/inquiries/ (email notification via Django SMTP / GoDaddy defaults in settings).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import HelmetWrapper from '../components/common/HelmetWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import inquiryService from '../services/inquiryService';

const TAB_ABOUT = 'about';
const TAB_CONTACT = 'contact';

/** Maps form topic key to Inquiry.category and Inquiry.subject (API model constraints). */
const TOPIC_TO_INQUIRY = {
  general: { category: 'general', subject: 'General Inquiry' },
  support: { category: 'general', subject: 'Technical Support' },
  business: { category: 'business', subject: 'Business Partnership' },
  advertising: { category: 'general', subject: 'Advertising' },
  feedback: { category: 'general', subject: 'Feedback' },
  other: { category: 'general', subject: 'Other' },
};

/* Light inputs on a slightly tinted panel so borders read clearly in both themes */
const inputBaseClass =
  'w-full px-3 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400 transition-shadow';

const AboutContact = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    topic: 'general',
    message: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    setContactForm((prev) => ({
      ...prev,
      name: fullName || prev.name,
      email: user.email || prev.email,
    }));
  }, [user]);

  const tabParam = searchParams.get('tab');
  const activeTab =
    tabParam === TAB_ABOUT || tabParam === TAB_CONTACT ? tabParam : TAB_CONTACT;

  const setTabInUrl = useCallback(
    (tab) => {
      if (tab === TAB_CONTACT) {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab: TAB_ABOUT }, { replace: true });
      }
    },
    [setSearchParams]
  );

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone, topic, message } = contactForm;
    if (!name.trim() || !email.trim() || !message.trim()) {
      showError('Please enter your name, email, and message.');
      return;
    }
    const mapped = TOPIC_TO_INQUIRY[topic] || TOPIC_TO_INQUIRY.general;
    setContactSubmitting(true);
    try {
      await inquiryService.createInquiry({
        category: mapped.category,
        name: name.trim(),
        email: email.trim(),
        phone: (phone || '').trim(),
        subject: mapped.subject,
        message: message.trim(),
      });
      showSuccess('Thanks! Your message was sent. We will get back to you soon.');
      setContactForm((prev) => ({
        ...prev,
        message: '',
        topic: 'general',
      }));
    } catch (err) {
      const data = err.response?.data;
      const msg =
        (data && data.message) ||
        (typeof data?.errors === 'object' && Object.values(data.errors).flat().join(' ')) ||
        err.message ||
        'Could not send your message. Please try again.';
      showError(typeof msg === 'string' ? msg : 'Could not send your message.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <HelmetWrapper>
        <title>About Us & Contact - PakPoints</title>
        <meta name="description" content="Learn about PakPoints - Pakistan's premier community platform connecting societies, businesses, and people. Contact us for support." />
        <meta property="og:title" content="About Us & Contact - PakPoints" />
        <meta property="og:description" content="Learn about PakPoints - Pakistan's premier community platform connecting societies, businesses, and people." />
      </HelmetWrapper>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 md:p-12">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Welcome to PakPoints
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Discover local societies, businesses, events, and more in your community.
            Connect with neighbors and explore what's happening around you.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/societies')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors"
            >
              Explore Societies
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-900 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8" aria-label="About and contact">
                <button
                  type="button"
                  onClick={() => setTabInUrl(TAB_ABOUT)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === TAB_ABOUT
                      ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                  About Us
                </button>
                <button
                  type="button"
                  onClick={() => setTabInUrl(TAB_CONTACT)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === TAB_CONTACT
                      ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                  Contact Us
                </button>
              </nav>
            </div>
          </div>

          {/* About Us Tab */}
          {activeTab === TAB_ABOUT && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 md:p-10">

              <div className="max-w-none">
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Our Mission</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    PakPoints is Pakistan's premier community platform designed to connect people, societies,
                    businesses, and communities across the country. We believe in fostering strong local
                    connections and making it easier for people to discover what's happening in their neighborhoods.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">What We Do</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg border border-blue-100/80 dark:border-gray-700">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">🏘️ Society Listings</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Comprehensive directory of residential societies with detailed information,
                        amenities, and community reviews.
                      </p>
                    </div>
                    <div className="bg-green-50 dark:bg-emerald-950/30 p-6 rounded-lg border border-green-100/80 dark:border-gray-700">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">🏢 Business Directory</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Find local businesses, services, and professionals in your area with
                        reviews and ratings from the community.
                      </p>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-950/30 p-6 rounded-lg border border-purple-100/80 dark:border-gray-700">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">🏠 Property Listings</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Real estate listings for buying, selling, and renting properties
                        with detailed information and photos.
                      </p>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-950/30 p-6 rounded-lg border border-orange-100/80 dark:border-gray-700">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">📅 Events & Activities</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Discover local events, community activities, and social gatherings
                        happening in your area.
                      </p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-950/30 p-6 rounded-lg border border-red-100/80 dark:border-gray-700">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">🔄 Free Exchange</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Community marketplace for free item exchanges, helping neighbors
                        share resources and reduce waste.
                      </p>
                    </div>
                    <div className="bg-indigo-50 dark:bg-indigo-950/30 p-6 rounded-lg border border-indigo-100/80 dark:border-gray-700">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">🎓 Educational Resources</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Directory of educational institutes, training centers, and learning
                        opportunities in your community.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Our Values</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Community First</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        We prioritize community needs and foster connections between neighbors.
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Trust & Safety</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        We maintain high standards for content quality and user safety.
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Innovation</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        We continuously improve our platform with new features and technologies.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Our Story</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    PakPoints was founded with a simple vision: to make it easier for Pakistanis to discover
                    and connect with their local communities. We noticed that while people were increasingly
                    moving to new areas, they often struggled to find information about their neighborhoods,
                    local businesses, and community events.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    Today, PakPoints serves thousands of users across Pakistan, helping them find everything
                    from the best local restaurants to community events and property listings. We're proud
                    to be building a platform that strengthens local communities and makes Pakistan more connected.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Join Our Community</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    Whether you're looking for a new home, want to discover local businesses, or simply
                    want to stay connected with your community, PakPoints is here to help. Join thousands
                    of Pakistanis who are already using our platform to make their communities stronger.
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg border border-blue-100/80 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Ready to get started?</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      Create your free account today and start exploring your community!
                    </p>
                    <button
                      type="button"
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors"
                    >
                      Sign Up Now
                    </button>
                  </div>
                </section>
              </div>
            </div>
          )}

          {/* Contact Us Tab — single card, classic two-column layout (readable in light & dark) */}
          {activeTab === TAB_CONTACT && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-8 py-8 md:px-10 md:py-10 border-b border-gray-200 dark:border-gray-700">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                  Contact Us
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-2xl">
                  Questions, feedback, or partnerships — fill out the form and we will get back to you.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-gray-200 dark:lg:divide-gray-700">
                {/* Left: Get in touch */}
                <div className="p-8 md:p-10 order-2 lg:order-1">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                    Get in Touch
                  </h2>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/50 p-3 rounded-lg">
                      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Email
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mt-1 mb-2">
                        General inquiries and support
                      </p>
                      <a
                        href="mailto:info@pakpoints.com"
                        className="text-blue-600 dark:text-blue-400 font-medium hover:underline break-all"
                      >
                        info@pakpoints.com
                      </a>
                    </div>
                  </div>
                  <p className="mt-8 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    We aim to reply within one business day. Your message is saved securely on our system
                    so our team can follow up.
                  </p>
                </div>

                {/* Right: Form */}
                <div className="p-8 md:p-10 bg-gray-50 dark:bg-gray-900/40 order-1 lg:order-2 border-t border-gray-200 dark:border-gray-700 lg:border-t-0">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                    Send us a Message
                  </h2>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                          Full name <span className="text-red-600 dark:text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          value={contactForm.name}
                          onChange={handleContactChange}
                          autoComplete="name"
                          className={inputBaseClass}
                          placeholder="Your name"
                          disabled={contactSubmitting}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                          Email <span className="text-red-600 dark:text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          value={contactForm.email}
                          onChange={handleContactChange}
                          autoComplete="email"
                          className={inputBaseClass}
                          placeholder="you@example.com"
                          disabled={contactSubmitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                        Phone <span className="text-gray-500 dark:text-gray-500 font-normal">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleContactChange}
                        autoComplete="tel"
                        className={inputBaseClass}
                        placeholder="+92 …"
                        disabled={contactSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-topic" className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                        Topic
                      </label>
                      <select
                        id="contact-topic"
                        name="topic"
                        value={contactForm.topic}
                        onChange={handleContactChange}
                        className={inputBaseClass}
                        disabled={contactSubmitting}
                      >
                        <option value="general">General inquiry</option>
                        <option value="support">Technical support</option>
                        <option value="business">Business partnership</option>
                        <option value="advertising">Advertising</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                        Message <span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={contactForm.message}
                        onChange={handleContactChange}
                        rows={5}
                        className={`${inputBaseClass} resize-y min-h-[120px]`}
                        placeholder="How can we help?"
                        disabled={contactSubmitting}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={contactSubmitting}
                      className="w-full sm:w-auto inline-flex justify-center items-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-2.5 px-6 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                    >
                      {contactSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending…
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </form>
                </div>
              </div>

              <div className="px-8 py-8 md:px-10 md:py-10 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/25">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      How do I create a listing on PakPoints?
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Sign up for a free account, open the section you need (Businesses, Properties, etc.),
                      and use the option to add a new listing.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Is PakPoints free to use?
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Yes — core features are free. We also offer optional premium options for businesses and power users.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      How can I report inappropriate content?
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Use the Report action on the listing or contact us at support@pakpoints.com.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default AboutContact;
